/**
 * <ConsensiGranulari /> — v1.61 (Fase 2 Privacy Lokky-style).
 *
 * 7 checkbox granulari (3 required, 4 opzionali) con base giuridica GDPR.
 * Il pattern segue quello di IVASSComplianceCheckboxes: controlled component,
 * builder helper `buildConsensiGranulariMeta()` per il payload al backend.
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, InfoIcon } from "lucide-react";
import { PRIVACY_CONSENT_VERSION } from "@/config/legalMeta";

// Alias per backward-compat con codice esistente (v1.61+)
export const CONSENT_TEXT_VERSION = PRIVACY_CONSENT_VERSION;

const FINALITA_LIST = [
  { key: "contratto",              required: true,  label: "Trattamento dati per elaborazione del preventivo assicurativo",
      help: "Base giuridica: art. 6.1.b GDPR (misure precontrattuali). Necessario per farti avere una quotazione." },
  { key: "contatto_commerciale",   required: true,  label: "Contatto commerciale (email, telefono, WhatsApp) post-quotazione",
      help: "Base giuridica: art. 6.1.b GDPR. Ti ricontatteremo per completare la proposta o rispondere a domande." },
  { key: "trasmissione_compagnie", required: true,  label: "Trasmissione dei dati alle compagnie assicurative partner",
      help: "Base giuridica: art. 6.1.b GDPR. Le compagnie devono ricevere i tuoi dati per formulare l'offerta." },
  { key: "dati_sanitari",          required: false, label: "Trattamento dati sanitari (obbligatorio solo per polizze Salute)",
      help: "Base giuridica: art. 9.2.a GDPR — consenso esplicito. Cifratura AES-256-GCM at-rest.", sanitario: true },
  { key: "marketing",              required: false, label: "Newsletter Colucci Assicuratori",
      help: "Base giuridica: art. 6.1.a GDPR (consenso libero, revocabile in ogni momento)." },
  { key: "profilazione",           required: false, label: "Profilazione per suggerimenti personalizzati",
      help: "Base giuridica: art. 6.1.a + art. 22 GDPR. Analizziamo i tuoi bisogni per proporti coperture pertinenti." },
  { key: "gruppo_partner",         required: false, label: "Comunicazione a società partner del gruppo",
      help: "Base giuridica: art. 6.1.f GDPR (legittimo interesse). Solo per finalità connesse ai servizi assicurativi." },
];

/** Builder payload da inviare al backend in `consensi_granulari`. */
export function buildConsensiGranulariMeta(state) {
  const out = {};
  const now = new Date().toISOString();
  FINALITA_LIST.forEach(({ key }) => {
    out[key] = {
      granted: !!state?.[key],
      timestamp: now,
      version: CONSENT_TEXT_VERSION,
    };
  });
  return out;
}

/** Predicato: tutti i required + (dati_sanitari se prodotto salute) sono spuntati. */
export function isConsensiOk(state, { includeSanitario = false } = {}) {
  const req = ["contratto", "contatto_commerciale", "trasmissione_compagnie"];
  if (includeSanitario) req.push("dati_sanitari");
  return req.every((k) => !!state?.[k]);
}

export default function ConsensiGranulari({
  value = {},
  onChange,
  testIdPrefix = "wizard",
  includeSanitario = false,
}) {
  const [expandedInfo, setExpandedInfo] = useState({});
  const list = FINALITA_LIST.filter((f) => !f.sanitario || includeSanitario);
  const toggle = (k) => onChange?.({ ...value, [k]: !value?.[k] });

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-consensi-granulari-block`}>
      <div className="text-xs uppercase font-heading tracking-wide text-[#163321]/70 mb-1">
        Consensi al trattamento dei dati (Reg. UE 2016/679)
      </div>
      {list.map((f) => (
        <div key={f.key}
              className="rounded-xl border border-[#E5E2DB] bg-white/60 p-3">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox"
                    checked={!!value?.[f.key]}
                    onChange={() => toggle(f.key)}
                    disabled={false}
                    data-testid={`${testIdPrefix}-consenso-${f.key}`}
                    className="mt-1 w-4 h-4 accent-[#163321] cursor-pointer shrink-0" />
            <span className="flex-1 text-[13px] leading-relaxed text-[#0F1F14]">
              {f.label}
              {f.required && (
                <span className="ml-2 text-[10px] text-red-700 font-semibold uppercase">obbligatorio</span>
              )}
              <button type="button"
                      onClick={(e) => { e.preventDefault(); setExpandedInfo(x => ({...x, [f.key]: !x[f.key]})); }}
                      className="ml-2 text-[#4A5E51] hover:text-[#163321] inline-flex align-middle"
                      data-testid={`${testIdPrefix}-consenso-info-${f.key}`}
                      aria-label="Info trattamento">
                <InfoIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          </label>
          {expandedInfo[f.key] && (
            <p className="mt-1.5 ml-7 text-[11.5px] text-[#4A5E51] leading-relaxed"
                data-testid={`${testIdPrefix}-consenso-help-${f.key}`}>
              {f.help}
            </p>
          )}
        </div>
      ))}
      <p className="text-[10.5px] text-[#4A5E51]/80 mt-2">
        Puoi revocare i consensi facoltativi in qualsiasi momento scrivendo a{" "}
        <a href="mailto:colucci@colucciassicuratori.com"
            className="text-[#163321] underline">colucci@colucciassicuratori.com</a>.
        La revoca dei consensi obbligatori impedisce la prosecuzione della richiesta.
      </p>
    </div>
  );
}
